import React, { CSSProperties } from 'react';
import { range, colors } from '../assets/variables';
import chroma from 'chroma-js';
import {
  prettifyIfNumber,
  colorRange,
  capitalizeFirstLetter,
} from './formatters';
import { Cell } from 'recharts';
import Styled from './customChartElements.style';
import * as _ from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/index';

export const themeToColor = (input) => {
  if (Array.isArray(input)) {
    return _.map(input, (theme) => colors[theme]);
  } else if (typeof input === 'string') {
    return colors[input];
  }

  return colors['primary'];
};

export const TicksDateRotated = (props) => {
  const { x, y, payload, period } = props;

  const tickText = () => {
    switch (period) {
      case 'month':
        return moment(payload.value).format('MMM YYYY');
      default:
        return moment(payload.value).format('DD MMM YYYY');
    }
  };

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        transform="rotate(-35)"
        x={0}
        y={7}
        dy={5}
        textAnchor="end"
        fill="#666"
      >
        {tickText()}
      </text>
    </g>
  );
};

export const ConversionTooltip = (e) => {
  if (e.payload[0]) {
    const conversion = Math.floor(
      (e.payload[0].value / e.payload[1].value) * 100
    );

    const wrapStyle = {
      backgroundColor: '#fff',
      padding: '10px',
      border: '1px solid #c8ced3',
      borderRadius: '2px',
      boxShadow: '-7px 8px 16px rgba(0, 0, 0, 0.10)',
      minWidth: '150px',
    };

    const datasets = e.payload.map((el, index) => {
      return (
        <div key={index}>
          <span
            style={{
              display: 'inline-block',
              height: '10px',
              width: '10px',
              backgroundColor: el.color,
              borderRadius: '20px',
              marginRight: '6px',
            }}
          />{' '}
          {el.value} {el.name}
        </div>
      );
    });

    return (
      <div style={wrapStyle}>
        {datasets}
        <hr className="mt-4" />
        <div>
          <span className="text-muted ">Conversion</span>
          <span className="float-right">{conversion}%</span>
        </div>
      </div>
    );
  }
};

export const ticks = (label) => {
  return moment(label).format('DD MMM YYYY');
};

export const TicksBold = (props) => {
  const { x, y, payload } = props;
  if (payload && payload.value) {
    return (
      <g transform={`translate(${x},${y})`}>
        <text fontWeight="bold" x={0} y={0} dy={5} textAnchor="end" fill="#666">
          {capitalizeFirstLetter(payload.value)}
        </text>
      </g>
    );
  }
};

export const TicksIcons = (props) => {
  const { x, y, payload, icons } = props;
  if (payload && payload.value) {
    if (icons) {
      return (
        <g transform={`translate(${x - 30},${y - 13})`}>
          <Icon size="30" icon={icons[props.index]}></Icon>
        </g>
      );
    }
    return (
      <g transform={`translate(${x},${y})`}>
        <text fontWeight="bold" x={0} y={0} dy={5} textAnchor="end" fill="#666">
          {capitalizeFirstLetter(payload.value)}
        </text>
      </g>
    );
  }
  return null;
};

export function TooltipDateWithPeriod(e, period, sort = false) {
  if (e.payload && e.payload[0]) {
    const wrapStyle = {
      backgroundColor: '#fff',
      padding: '10px',
      border: '1px solid #c8ced3',
      borderRadius: '2px',
      boxShadow: '-7px 8px 16px rgba(0, 0, 0, 0.10)',
      minWidth: '180px',
    };

    let datasetsArray = e.payload.map((el) => {
      return {
        color: el.color,
        value: el.value,
        name: el.name,
      };
    });

    if (sort) {
      datasetsArray = _.orderBy(datasetsArray, ['value'], ['desc']);
    }

    const datasets = datasetsArray.map((el, index) => {
      return (
        <div key={index}>
          <span
            style={{
              display: 'inline-block',
              height: '10px',
              width: '10px',
              backgroundColor: el.color,
              borderRadius: '20px',
              marginRight: '6px',
            }}
          />{' '}
          {el.value} {el.name}
        </div>
      );
    });

    const dateLabel = e.label;

    const renderLabel = () => {
      switch (period) {
        case 'month':
          return moment(dateLabel).format('MMM YYYY');
        default:
          return moment(dateLabel).format('DD MMM YYYY');
      }
    };

    return (
      <div style={wrapStyle}>
        {datasets}
        <hr className="mt-3" />
        <div>
          <span className="text-muted ">Date</span>
          <span className="float-right">{renderLabel()}</span>
        </div>
      </div>
    );
  }
}

export const RenderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  donut,
}) => {
  const RADIAN = Math.PI / 180;
  const radius = outerRadius + outerRadius * 0.12;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      style={{
        pointerEvents: 'none',
        fontSize: '13px',
      }}
      x={x}
      y={y}
      fill="#0b2641"
      textAnchor="middle"
      dominantBaseline="central"
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export const TooltipSimple = (e, xLabel) => {
  if (e.payload && e.payload[0]) {
    const wrapStyle = {
      backgroundColor: '#fff',
      padding: '10px',
      border: '1px solid #c8ced3',
      borderRadius: '2px',
      boxShadow: '-7px 8px 16px rgba(0, 0, 0, 0.10)',
      minWidth: '180px',
    };

    const datasets = e.payload.map((el, index) => {
      return (
        <Styled.PieLegendLi key={index}>
          <Styled.PieLegendDot
            style={{
              display: 'inline-block',
              height: '10px',
              width: '10px',
              backgroundColor: el.color,
              borderRadius: '20px',
              marginRight: '6px',
            }}
          />
          <span>{el.name}</span>
          <Styled.PieLegendValue>
            {prettifyIfNumber(el.value)}
          </Styled.PieLegendValue>
        </Styled.PieLegendLi>
      );
    });

    return (
      <div style={wrapStyle}>
        <Styled.BarToolTiplabel>
          {e.label} ({xLabel})
        </Styled.BarToolTiplabel>
        <Styled.PieLegendWrap>{datasets}</Styled.PieLegendWrap>
      </div>
    );
  }
};

export const PieTooltip = (props) => {
  const { payload } = props;

  const UseColors = () => _.get(props, 'payload[0].payload.fill');

  const wrapStyle: CSSProperties = {
    backgroundColor: '#fff',
    padding: '10px',
    border: '1px solid #c8ced3',
    borderRadius: '2px',
    boxShadow: '-7px 8px 16px rgba(0, 0, 0, 0.10)',
    maxWidth: '300px',
  };
  const liStyle: CSSProperties = {
    width: '100%',
    display: 'flex',
    flexWrap: 'nowrap',
    flexDirection: 'row',
  };

  const dotStyle: CSSProperties = {
    height: '10px',
    width: '10px',
    backgroundColor: UseColors(),
    borderRadius: '20px',
    minWidth: '10px',
    marginTop: '5px',
    marginRight: '10px',
  };

  const textStyle: CSSProperties = {
    width: '100%',
    flexGrow: 2,
    marginRight: '10px',
  };

  const datasets = payload.map((el, index) => {
    return (
      <li style={liStyle} key={index}>
        <span style={dotStyle} />
        <span style={textStyle}>{capitalizeFirstLetter(el.name)}</span>{' '}
        {prettifyIfNumber(el.value)}
      </li>
    );
  });
  return <ul style={wrapStyle}>{datasets}</ul>;
};

const generateColorsArray = (color: string, length: number): Array<string> => {
  return chroma
    .scale([color, chroma(color).luminance(0.7)])
    .mode('lch')
    .colors(length);
};

export const UseColors = ({ data, color }) => {
  // generate colors grom array

  if (Array.isArray(color)) {
    return data.map((entry, index) => {
      const fill =
        color.length > 1
          ? color[index % color.length]
          : colorRange(
              index,
              generateColorsArray(
                _.get(color, '[0]', colors['c1']),
                data.length
              )
            );
      return <Cell key="cell" fill={fill} />;
    });
  }

  // if only one color exist
  else if (typeof color === 'string') {
    return data.map((entry, index) => (
      <Cell
        key="cell"
        fill={colorRange(index, generateColorsArray(color, data.length))}
      />
    ));
  }
  return data.map((entry, index) => (
    <Cell key="cell" fill={range[index % range.length]} />
  ));
};

const colorArray = (color, data) => {
  if (Array.isArray(color)) {
    return color.length > 1
      ? color
      : generateColorsArray(_.get(color, '[0]', colors['c1']), data.length);
  }
  // if only one color exist
  else if (typeof color === 'string') {
    return generateColorsArray(color, data.length);
  }
  return range;
};

const dotColor = (index: number, color, data) => {
  return colorRange(index, colorArray(color, data));
};

export const CustomPieLegend = ({ data, color, totals }) => {
  if (data && data.length) {
    return (
      <Styled.PieLegendWrap>
        {data.map((el, index) => {
          return (
            <Styled.PieLegendLi key={index}>
              <Styled.PieLegendDot color={dotColor(index, color, data)} />
              <span>{capitalizeFirstLetter(el.name)}</span>
              <Styled.PieLegendValue
                title={`${((el.dataset_0 / totals) * 100).toFixed(0)}%`}
              >
                {prettifyIfNumber(el.dataset_0)}
              </Styled.PieLegendValue>
            </Styled.PieLegendLi>
          );
        })}
      </Styled.PieLegendWrap>
    );
  }
};

export const CustomBarLegend = ({ data, color, current, original }) => {
  const [t] = useTranslation('common');

  if (data && data.length) {
    return (
      <Styled.BarLegendWrap>
        <Styled.BarLegendLi>
          <Styled.PieLegendDot />
          <Styled.LabelName></Styled.LabelName>
          <div
            style={{
              display: 'flex',
              flex: '0 1 70px',
              color: '#0b2641',
              fontWeight: 600,
            }}
          >
            {original}
          </div>
          <Styled.ValueNumber style={{ color: '#0b2641' }}>
            {current}
          </Styled.ValueNumber>
        </Styled.BarLegendLi>
        {data.map((el, index) => {
          return (
            <Styled.BarLegendLi key={index}>
              <Styled.PieLegendDot color={dotColor(index, color, data)} />
              <Styled.LabelName>
                {capitalizeFirstLetter(t(el.label))}
              </Styled.LabelName>

              <div style={{ fontWeight: 600, color: '#0b2641' }}>
                {el.originalValue || el.originalValue === 0
                  ? prettifyIfNumber(el.originalValue)
                  : 'NaN'}
              </div>

              <Styled.ValueNumber
                style={{
                  color: '#246b13',
                  display: 'flex',
                  flex: '0 1 70px',
                }}
              >
                {el.value || el.value === 0
                  ? prettifyIfNumber(el.value)
                  : 'NaN'}
              </Styled.ValueNumber>
            </Styled.BarLegendLi>
          );
        })}
      </Styled.BarLegendWrap>
    );
  }
};

export const CustomBarNegativeLegend = ({ data, color }) => {
  const [t] = useTranslation('common');

  if (data && data.length) {
    return (
      <Styled.BarLegendWrap>
        {data.map((el, index) => {
          return (
            <Styled.BarLegendLi key={index}>
              <Styled.PieLegendDot color={dotColor(index, color, data)} />
              <Styled.LabelName>
                {capitalizeFirstLetter(t(el.label))}
              </Styled.LabelName>

              <Styled.ValueNumber
                style={{
                  color: '#0b2641',
                  display: 'flex',
                  flex: '0 1 70px',
                }}
              >
                {el.value}
              </Styled.ValueNumber>
            </Styled.BarLegendLi>
          );
        })}
      </Styled.BarLegendWrap>
    );
  }
};
